import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAddCommentMutation, useCommentsQuery, useMeQuery, useMembersQuery } from '@platform/state/api';
import { ErrorBanner, Spinner } from '@platform/ui';
import { CommentBubble } from './comments/CommentBubble';
import { CommentComposer } from './comments/CommentComposer';
import type { IRecordTabProps } from './IRecordTabProps';
import { useRecordRefresh } from './useRecordRefresh';

/**
 * Вкладка «Коментарі» (UIUX forms/tabs, прототип v4): стрічка до 760px від найстаріших, власні коментарі — акцентною бульбашкою;
 * порожньо — «Коментарів ще немає. Почніть обговорення нижче.»; поле нового коментаря під стрічкою (згадки, Ctrl+Enter).
 */
export function CommentsTab({ ws, record }: IRecordTabProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useCommentsQuery(record.id);
  const { data: me } = useMeQuery();
  const { data: members = [] } = useMembersQuery(ws, { skip: !ws });
  const [add, { isLoading: sending, error }] = useAddCommentMutation();
  const refresh = useRecordRefresh(record);
  const comments = useMemo(() => [...(data ?? [])].sort((a, b) => a.at.localeCompare(b.at)), [data]);
  const names = useMemo(() => members.map((m) => m.user.name), [members]);
  if (isLoading) return <Spinner />;
  const send = (body: string, mentions: string[]) =>
    add({ recordId: record.id, body: { body, mentions: mentions.length ? mentions : undefined } }).unwrap().then(() => { refresh(); return true; }).catch(() => false);
  return (
    <div className="rtab-comments">
      {comments.map((c) => <CommentBubble key={c.id} comment={c} mine={c.author.id === me?.id} names={names} />)}
      {!comments.length && <p className="rtab-comments__empty">{t('recordPage.comments.empty')}</p>}
      {record.permissions?.comment !== false && <CommentComposer ws={ws} sending={sending} onSend={send} />}
      <ErrorBanner error={error} />
    </div>
  );
}
